import { createFileRoute, Link } from "@tanstack/react-router";
import { SeguirCriterioButton } from "@/components/watchlist/SeguirCriterioButton";
import { AppShell } from "@/components/layout/AppShell";
import { AlertCircle, ArrowLeft, ExternalLink, Share2 } from "lucide-react";
import { licitacionesService } from "@/services/licitaciones.service";
import type { Licitacion } from "@/types";
import { useCallback, useEffect, useState } from "react";
import { LicitacionDetail } from "@/components/licitaciones/LicitacionDetail";
import { Button } from "@/components/ui/button";
import { shareTender } from "@/lib/licitaciones";
import { toast } from "sonner";
import { ApiError } from "@/services/api/errors";
import { mercadoPublicoUrl } from "@/lib/licitaciones";

export const Route = createFileRoute("/licitaciones/$id")({
  head: () => ({ meta: [{ title: "Detalle de Licitación — LicitaPro" }] }),
  component: Page,
  notFoundComponent: () => <div className="p-10">Licitación no encontrada</div>,
});

export function Page() {
  const { id } = Route.useParams();
  const [l, setL] = useState<Licitacion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"unauthorized" | "not-found" | "generic" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await licitacionesService.getById(id);
      if (!result) {
        setL(null);
        setError("not-found");
        return;
      }
      setL(result);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403)) {
        setError("unauthorized");
      } else if (caught instanceof ApiError && caught.status === 404) {
        setError("not-found");
      } else {
        setError("generic");
      }
      setL(null);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <AppShell title="Cargando…">
        <div className="space-y-3 py-12" aria-label="Cargando licitación">
          <div className="h-8 animate-pulse rounded bg-muted" />
          <div className="h-28 animate-pulse rounded bg-muted" />
          <div className="h-56 animate-pulse rounded bg-muted" />
        </div>
      </AppShell>
    );
  }
  if (error)
    return (
      <AppShell title="Error al cargar">
        <div className="grid place-items-center gap-3 py-16 text-center">
          <AlertCircle className="size-8 text-destructive" />
          <div>
            <p className="font-medium">
              {error === "unauthorized"
                ? "Tu sesión no permite cargar esta licitación"
                : error === "not-found"
                  ? "Licitación no encontrada"
                  : "No se pudo cargar la licitación"}
            </p>
            <p className="text-sm text-muted-foreground">
              {error === "unauthorized"
                ? "Vuelve a iniciar sesión y luego inténtalo nuevamente."
                : error === "not-found"
                  ? "El registro pudo haber sido eliminado o el enlace no es válido."
                  : "Revisa la conexión e inténtalo nuevamente."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/licitaciones">Volver al listado</Link>
            </Button>
            {error === "unauthorized" && (
              <Button
                onClick={() => {
                  window.sessionStorage.setItem("tgtone_return_to", window.location.pathname);
                  window.location.reload();
                }}
              >
                Volver a iniciar sesión
              </Button>
            )}
            {error !== "not-found" && (
              <Button variant="outline" onClick={load}>
                Reintentar
              </Button>
            )}
          </div>
        </div>
      </AppShell>
    );
  if (!l) {
    return (
      <AppShell title="No encontrada">
        <div className="p-10 text-center text-sm text-muted-foreground">
          Licitación no encontrada.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={l.codigo}
      subtitle={l.organismo}
      actions={
        <div className="flex items-center gap-2">
          <SeguirCriterioButton
            opciones={[
              { kind: "organismo", valor: l.organismo, label: "Organismo" },
              ...(l.region ? [{ kind: "region" as const, valor: l.region, label: "Región" }] : []),
              ...(l.categoriaUnspsc ?? [])
                .slice(0, 3)
                .map((c) => ({ kind: "categoria" as const, valor: c, label: "Categoría UNSPSC" })),
            ]}
          />
          <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
            <a href={mercadoPublicoUrl(l.codigo)} target="_blank" rel="noreferrer">
              <ExternalLink /> Ficha oficial
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await shareTender(l);
                toast.success("Licitación compartida");
              } catch {
                toast.error("No fue posible compartir");
              }
            }}
          >
            <Share2 /> Compartir
          </Button>
        </div>
      }
    >
      <Link
        to="/licitaciones"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> Volver a licitaciones
      </Link>
      <main>
        <LicitacionDetail licitacion={l} onChange={setL} />
      </main>
    </AppShell>
  );
}
